import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { RefreshCw, ExternalLink, CheckCircle2, XCircle, Copy, Plus, X, Lock } from "lucide-react";

function ApartmentICalCard({ apt, onSaved }) {
  const [urls, setUrls] = useState(apt.ical_urls || []);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [external, setExternal] = useState([]);
  const [block, setBlock] = useState({ start: "", end: "", summary: "" });
  const [adding, setAdding] = useState(false);
  const lastSync = apt.ical_last_sync;

  const loadExternal = useCallback(async () => {
    try {
      const r = await api.get(`/admin/ical/external/${apt.id}`);
      setExternal(r.data);
    } catch (e) {
      console.error("Failed to load external bookings:", e);
    }
  }, [apt.id]);

  useEffect(() => {
    loadExternal();
  }, [loadExternal]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/apartments/${apt.id}`, { ical_urls: urls.filter(Boolean) });
      toast.success("iCal URLs saved");
      onSaved?.();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await api.post(`/admin/ical/sync/${apt.id}`);
      toast.success(`Synced ${r.data.total_events} event(s)`);
      onSaved?.();
      loadExternal();
    } catch (e) {
      toast.error("Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const addBlock = async () => {
    if (!block.start || !block.end) {
      toast.error("Both start and end dates required");
      return;
    }
    setAdding(true);
    try {
      await api.post(`/admin/blocks/${apt.id}`, block);
      toast.success("Manual block added");
      setBlock({ start: "", end: "", summary: "" });
      loadExternal();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add block");
    } finally {
      setAdding(false);
    }
  };

  const deleteExternal = async (ev) => {
    const isIcal = ev.source_url && ev.source_url !== "manual";
    const msg = isIcal
      ? `Διαγραφή αυτής της εγγραφής iCal;\n\nΘα ξανα-εμφανιστεί στο επόμενο sync αν είναι ακόμη στο feed της πηγής (${ev.source_url.includes("booking.com") ? "Booking.com" : "Airbnb"}). Χρήσιμο μόνο για stale/ακυρωμένες κρατήσεις.`
      : "Διαγραφή χειροκίνητου block;";
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/admin/ical/external/${ev.id}`);
      toast.success("Deleted");
      loadExternal();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const exportUrl = `${process.env.REACT_APP_BACKEND_URL}/api/apartments/${apt.id}/calendar.ics`;

  const copyExport = () => {
    navigator.clipboard.writeText(exportUrl);
    toast.success("Export URL copied — paste it into Booking.com / Airbnb / Vrbo");
  };

  const updateUrl = (i, v) => setUrls(urls.map((u, idx) => (idx === i ? v : u)));
  const removeUrl = (i) => setUrls(urls.filter((_, idx) => idx !== i));
  const addUrl = () => setUrls([...urls, ""]);

  const input = "w-full bg-[#F5F2EB] border border-[#E9E2D3] rounded-xl px-3 py-2.5 text-[#1A2B4C] focus:outline-none focus:ring-2 focus:ring-[#1A2B4C]/30 text-sm";

  return (
    <div className="bg-white rounded-2xl border border-[#E9E2D3] p-6 mb-6">
      <div className="flex items-start justify-between mb-5">
        <div>
          <h3 className="font-serif text-xl text-[#1A2B4C]">{apt.name_en}</h3>
          {lastSync ? (
            <p className="text-xs text-[#6B705C] mt-1 flex items-center gap-1">
              {lastSync.ok ? (
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
              ) : (
                <XCircle className="w-3.5 h-3.5 text-red-500" />
              )}
              Last sync: {new Date(lastSync.synced_at).toLocaleString()} · {lastSync.total_events} events
            </p>
          ) : (
            <p className="text-xs text-[#6B705C] mt-1">Never synced</p>
          )}
        </div>
        <button
          onClick={sync}
          disabled={syncing || urls.length === 0}
          data-testid={`sync-ical-${apt.id}`}
          className="inline-flex items-center gap-2 bg-[#1A2B4C] text-white px-4 py-2 rounded-full text-xs uppercase tracking-widest hover:-translate-y-0.5 transition-transform disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} />
          {syncing ? "Syncing..." : "Sync now"}
        </button>
      </div>

      {/* Inbound iCal URLs (FROM Booking/Airbnb) */}
      <div className="mb-5">
        <label className="text-xs uppercase tracking-widest text-[#6B705C] mb-2 block">
          Inbound iCal URLs (import from Booking.com / Airbnb / Vrbo)
        </label>
        <div className="space-y-2 mb-2">
          {urls.map((u, i) => (
            <div key={i} className="flex gap-2">
              <input
                className={input}
                value={u}
                onChange={(e) => updateUrl(i, e.target.value)}
                placeholder="https://ical.booking.com/v1/export/..."
                data-testid={`ical-url-${apt.id}-${i}`}
              />
              <button
                onClick={() => removeUrl(i)}
                className="text-red-500 hover:bg-red-50 rounded-lg px-2"
                title="Remove"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-3">
          <button
            onClick={addUrl}
            className="text-xs text-[#1A2B4C] border border-[#E9E2D3] px-3 py-1.5 rounded-full hover:bg-[#1A2B4C] hover:text-white transition-all"
          >
            + Add URL
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid={`save-ical-${apt.id}`}
            className="text-xs bg-[#1A2B4C] text-white px-4 py-1.5 rounded-full uppercase tracking-widest disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save URLs"}
          </button>
        </div>
      </div>

      {/* Outbound iCal */}
      <div className="mb-5 pt-5 border-t border-[#E9E2D3]">
        <label className="text-xs uppercase tracking-widest text-[#6B705C] mb-2 block">
          Outbound iCal URL (give this to Booking.com / Airbnb so they see your direct bookings)
        </label>
        <div className="flex gap-2">
          <input
            readOnly
            value={exportUrl}
            className={input + " bg-white"}
            data-testid={`export-url-${apt.id}`}
          />
          <button
            onClick={copyExport}
            className="inline-flex items-center gap-1 bg-[#6B705C] text-white px-4 py-2.5 rounded-xl text-xs"
          >
            <Copy className="w-3.5 h-3.5" />
            Copy
          </button>
          <a
            href={exportUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 border border-[#E9E2D3] px-3 py-2.5 rounded-xl text-xs text-[#6B705C] hover:bg-[#F5F2EB]"
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>

      {/* External bookings list + Manual blocks */}
      <div className="pt-5 border-t border-[#E9E2D3]">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs uppercase tracking-widest text-[#6B705C]">
            Blocked dates ({external.length})
          </p>
          <span className="text-[10px] text-[#6B705C]">
            <Lock className="w-3 h-3 inline mr-1" />
            iCal-synced · ✕ διαγραφή
          </span>
        </div>

        {/* Add manual block */}
        <div className="bg-[#F5F2EB] rounded-xl p-3 mb-3">
          <p className="text-[11px] uppercase tracking-widest text-[#6B705C] mb-2">
            Προσθήκη χειροκίνητου block (συντήρηση / προσωπική χρήση)
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
            <input
              type="date"
              value={block.start}
              onChange={(e) => setBlock({ ...block, start: e.target.value })}
              className="bg-white border border-[#E9E2D3] rounded-lg px-2 py-1.5 text-sm"
              data-testid={`block-start-${apt.id}`}
            />
            <input
              type="date"
              value={block.end}
              onChange={(e) => setBlock({ ...block, end: e.target.value })}
              className="bg-white border border-[#E9E2D3] rounded-lg px-2 py-1.5 text-sm"
              data-testid={`block-end-${apt.id}`}
            />
            <input
              type="text"
              value={block.summary}
              onChange={(e) => setBlock({ ...block, summary: e.target.value })}
              placeholder="Σημείωση (προαιρετικό)"
              className="bg-white border border-[#E9E2D3] rounded-lg px-2 py-1.5 text-sm sm:col-span-1"
              data-testid={`block-summary-${apt.id}`}
            />
            <button
              onClick={addBlock}
              disabled={adding}
              data-testid={`add-block-${apt.id}`}
              className="inline-flex items-center justify-center gap-1.5 bg-[#1A2B4C] text-white px-3 py-1.5 rounded-lg text-xs uppercase tracking-widest disabled:opacity-50"
            >
              <Plus className="w-3 h-3" />
              {adding ? "..." : "Προσθήκη"}
            </button>
          </div>
        </div>

        {external.length === 0 ? (
          <p className="text-sm text-[#6B705C]">No external bookings or manual blocks yet.</p>
        ) : (
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {external.map((e) => {
              const isManual = e.source_url === "manual";
              return (
                <div
                  key={e.id}
                  data-testid={`block-row-${e.id}`}
                  className="bg-[#F5F2EB] rounded-lg px-3 py-2 text-sm flex items-center justify-between gap-2"
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {isManual ? (
                      <span className="text-[10px] uppercase tracking-widest bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded shrink-0">
                        manual
                      </span>
                    ) : (
                      <Lock className="w-3 h-3 text-[#6B705C] shrink-0" />
                    )}
                    <span className="text-[#1A2B4C] whitespace-nowrap">
                      {e.start} → {e.end}
                    </span>
                    <span className="text-xs text-[#6B705C] truncate">{e.summary}</span>
                  </div>
                  <button
                    onClick={() => deleteExternal(e)}
                    data-testid={`del-block-${e.id}`}
                    className="w-7 h-7 rounded-full hover:bg-red-50 text-red-500 flex items-center justify-center shrink-0"
                    title="Διαγραφή"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ICalTab() {
  const [apts, setApts] = useState([]);
  const [syncingAll, setSyncingAll] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/apartments");
      setApts(r.data);
    } catch (e) {
      console.error("Failed to load apartments:", e);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const syncAll = async () => {
    setSyncingAll(true);
    try {
      await api.post("/admin/ical/sync-all");
      toast.success("All apartments synced");
      load();
    } catch (e) {
      toast.error("Sync failed");
    } finally {
      setSyncingAll(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif text-2xl text-[#1A2B4C]">iCal Calendar Sync</h2>
          <p className="text-sm text-[#6B705C]">
            Import bookings from Booking.com / Airbnb / Vrbo and share your direct bookings back to them.
            <br />
            Auto-syncs every 30 minutes.
          </p>
        </div>
        <button
          onClick={syncAll}
          disabled={syncingAll}
          data-testid="sync-all-ical"
          className="inline-flex items-center gap-2 bg-[#1A2B4C] text-white px-5 py-3 rounded-full text-sm uppercase tracking-widest hover:-translate-y-0.5 transition-transform disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${syncingAll ? "animate-spin" : ""}`} />
          {syncingAll ? "Syncing..." : "Sync all"}
        </button>
      </div>

      {apts.map((a) => (
        <ApartmentICalCard key={a.id} apt={a} onSaved={load} />
      ))}
    </div>
  );
}
